/**
 * API functions for role management.
 * Feature: System Administration Interface (001-admin-settings)
 * 
 * Uses fetcher utility with automatic JWT token inclusion.
 * System-role changes and user management require Admin; programme/project
 * access-role assignment also accepts Portfolio Manager (enforced by backend).
 */

import { fetcher } from '../utils/fetcher';
import type {
  UserWithRoles,
  SystemRoleUpdate,
  ProjectRole,
  ProjectRoleCreate,
  ProjectRoleUpdate,
  ProgrammeRole,
  ProgrammeRoleCreate,
  ProgrammeRoleUpdate
} from '../types/role';

const API_URL = '/api/v1/users';

/**
 * Get all users with their role assignments.
 * Admin only.
 */
export const getUsersWithRoles = async (): Promise<UserWithRoles[]> => {
  return await fetcher(`${API_URL}/roles`);
};

/**
 * Update user's system role.
 * Prevents self-demotion and last-admin removal.
 * Admin only.
 */
export const updateUserSystemRole = async (
  userId: string,
  roleUpdate: SystemRoleUpdate
): Promise<UserWithRoles> => {
  return await fetcher(`${API_URL}/${userId}/system-role`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(roleUpdate)
  });
};

/**
 * Get all project role assignments with optional filtering.
 * Admin only.
 */
export const getProjectRoles = async (
  userId?: string,
  projectId?: string
): Promise<ProjectRole[]> => {
  const params = new URLSearchParams();
  if (userId) params.append('user_id', userId);
  if (projectId) params.append('project_id', projectId);
  
  const queryString = params.toString();
  return await fetcher(`${API_URL}/project-roles/read${queryString ? `?${queryString}` : ''}`);
};

/**
 * Assign project role to user.
 * Admin only.
 */
export const createProjectRole = async (
  roleCreate: ProjectRoleCreate
): Promise<ProjectRole> => {
  return await fetcher(`${API_URL}/project-roles/create`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(roleCreate)
  });
};

/**
 * Update the level (Editor/Viewer) of an existing project role assignment.
 * Admin only.
 */
export const updateProjectRole = async (
  roleId: string,
  roleUpdate: ProjectRoleUpdate
): Promise<ProjectRole> => {
  return await fetcher(`${API_URL}/project-roles/update/${roleId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(roleUpdate)
  });
};

/**
 * Remove project role assignment.
 * Admin only.
 */
export const deleteProjectRole = async (roleId: string): Promise<boolean> => {
  return await fetcher(`${API_URL}/project-roles/delete/${roleId}`, {
    method: 'DELETE'
  });
};

/**
 * Get all programme role assignments with optional filtering.
 * Admin only.
 */
export const getProgrammeRoles = async (
  userId?: string,
  programmeId?: string
): Promise<ProgrammeRole[]> => {
  const params = new URLSearchParams();
  if (userId) params.append('user_id', userId);
  if (programmeId) params.append('programme_id', programmeId);
  
  const queryString = params.toString();
  return await fetcher(`${API_URL}/programme-roles/read${queryString ? `?${queryString}` : ''}`);
};

/**
 * Assign programme role to user.
 * Admin only.
 */
export const createProgrammeRole = async (
  roleCreate: ProgrammeRoleCreate
): Promise<ProgrammeRole> => {
  return await fetcher(`${API_URL}/programme-roles/create`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(roleCreate)
  });
};

/**
 * Update the level (Editor/Viewer) of an existing programme role assignment.
 * Admin or Portfolio Manager.
 */
export const updateProgrammeRole = async (
  roleId: string,
  roleUpdate: ProgrammeRoleUpdate
): Promise<ProgrammeRole> => {
  return await fetcher(`${API_URL}/programme-roles/update/${roleId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(roleUpdate)
  });
};

/**
 * Remove programme role assignment.
 * Admin or Portfolio Manager.
 */
export const deleteProgrammeRole = async (roleId: string): Promise<boolean> => {
  return await fetcher(`${API_URL}/programme-roles/delete/${roleId}`, {
    method: 'DELETE'
  });
};
